import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {
  disableTeleconsult,
  enableTeleconsult,
  getTeleconsultStatus,
  TeleconsultStatus,
} from '../../services/teleconsultService';

/** Espelha regras do backend (teleconsultService): não habilitar em status terminais. */
const TELECONSULT_BLOCKED_STATUSES = new Set([
  'cancelled',
  'no_show',
  'completed',
  'rescheduled',
]);

function canOfferTeleconsultForStatus(status?: string | null): boolean {
  if (status == null || status === '') return true;
  return !TELECONSULT_BLOCKED_STATUSES.has(status);
}

interface TeleconsultButtonProps {
  appointmentId: string;
  /** Status do agendamento (clinic_appointments.status) — oculta "habilitar" quando terminal. */
  appointmentStatus?: string | null;
  /** Compact: usado em linhas da agenda (ícone só). */
  compact?: boolean;
}

/**
 * Botão dual-purpose para uma consulta:
 *  - Se habilitada: abre sala (rota interna) + permite copiar link do paciente / desabilitar
 *  - Se não habilitada: oferece "Habilitar teleconsulta"
 *
 * Aparece imediatamente; faz GET de status em background.
 */
const TeleconsultButton: React.FC<TeleconsultButtonProps> = ({
  appointmentId,
  appointmentStatus,
  compact,
}) => {
  const [status, setStatus] = useState<TeleconsultStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const s = await getTeleconsultStatus(appointmentId);
      setStatus(s);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao carregar status.');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEnable = async () => {
    setEnabling(true);
    setError('');
    try {
      await enableTeleconsult(appointmentId);
      await load();
      setDialogOpen(true);
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Não foi possível habilitar.';
      setError(msg);
      if (/configurada/i.test(msg)) {
        // Mantém dialog fechado, mostra alerta inline.
      }
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    setEnabling(true);
    try {
      await disableTeleconsult(appointmentId);
      await load();
      setDialogOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao desabilitar.');
    } finally {
      setEnabling(false);
    }
  };

  const handleOpenRoom = () => {
    window.open(`/clinic/teleconsulta/${appointmentId}`, '_blank', 'noopener');
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignora
    }
  };

  const enabled = !!status?.enabled;
  const patientUrl = status?.patientUrl || '';
  const statusAllowsNew = canOfferTeleconsultForStatus(appointmentStatus);
  const roomActionDisabled = enabled && !statusAllowsNew;

  const teleconsultDialog = (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Teleconsulta</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Envie o link abaixo ao paciente. Ele abre a sala em qualquer navegador,
            sem precisar de conta. O link só funciona próximo ao horário da consulta.
          </Typography>

          <TextField
            label="Link do paciente"
            value={patientUrl}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                  <IconButton onClick={() => handleCopy(patientUrl)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />

          {status?.patientTokenExpiresAt && (
            <Typography variant="caption" color="text.secondary">
              Válido até {new Date(status.patientTokenExpiresAt).toLocaleString('pt-BR')}.
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<VideoCallIcon />}
              onClick={handleOpenRoom}
              disabled={roomActionDisabled}
              sx={{
                background: roomActionDisabled
                  ? '#cbd5e1'
                  : 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                textTransform: 'none',
              }}
            >
              Entrar na sala
            </Button>
            <Button
              variant="text"
              color="error"
              startIcon={<PowerSettingsNewIcon />}
              onClick={handleDisable}
              disabled={enabling}
              sx={{ textTransform: 'none' }}
            >
              Desabilitar teleconsulta
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );

  // ------- Render compacto -------
  if (compact) {
    if (loading && !status) {
      return (
        <>
          <CircularProgress size={16} sx={{ ml: 1 }} />
          {teleconsultDialog}
        </>
      );
    }
    if (!statusAllowsNew && !enabled) {
      return null;
    }
    if (enabled) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip
              title={
                roomActionDisabled
                  ? 'Agendamento encerrado — desabilite a teleconsulta se não for mais usar'
                  : 'Abrir teleconsulta'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenRoom}
                  disabled={roomActionDisabled}
                  sx={{ color: roomActionDisabled ? '#cbd5e1' : '#14B8A6' }}
                >
                  <VideoCallIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Copiar link do paciente">
              <IconButton size="small" onClick={() => setDialogOpen(true)} sx={{ color: '#64748b' }}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 0.5, py: 0, maxWidth: 280 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {teleconsultDialog}
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
        <Tooltip title="Habilitar teleconsulta">
          <span>
            <IconButton size="small" onClick={handleEnable} disabled={enabling}>
              {enabling ? <CircularProgress size={16} /> : <VideoCallIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {error && (
          <Alert severity="error" sx={{ mt: 0.5, py: 0, maxWidth: 280 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {teleconsultDialog}
      </Box>
    );
  }

  // ------- Render padrão (ex.: modal da agenda — nunca ficar só o título sem conteúdo) -------
  if (!compact && loading && !status && !error) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <CircularProgress size={22} sx={{ color: '#14B8A6' }} />
          <Typography variant="body2" color="text.secondary">
            Carregando teleconsulta…
          </Typography>
        </Box>
        {teleconsultDialog}
      </>
    );
  }

  if (!statusAllowsNew && !enabled) {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Teleconsulta por vídeo só pode ser <strong>habilitada</strong> em consultas ainda ativas (ex.: agendada,
          confirmada, em atendimento). Para consultas <strong>canceladas</strong>, <strong>concluídas</strong>,{' '}
          <strong>não comparecidas</strong> ou <strong>reagendadas</strong>, não há ação aqui — altere o status
          acima se precisar voltar a usar a sala ou abra um novo agendamento.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {teleconsultDialog}
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {!enabled && (
          <Button
            variant="outlined"
            size="small"
            startIcon={enabling ? <CircularProgress size={16} /> : <VideoCallIcon />}
            disabled={enabling}
            onClick={handleEnable}
            sx={{
              borderColor: '#14B8A6',
              color: '#0f766e',
              textTransform: 'none',
              '&:hover': { borderColor: '#0f766e', bgcolor: 'rgba(20,184,166,0.06)' },
            }}
          >
            Habilitar teleconsulta
          </Button>
        )}
        {enabled && (
          <>
            <Button
              variant="contained"
              size="small"
              startIcon={<VideoCallIcon />}
              onClick={handleOpenRoom}
              disabled={roomActionDisabled}
              sx={{
                background: roomActionDisabled
                  ? '#cbd5e1'
                  : 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Iniciar teleconsulta
            </Button>
            <Tooltip title="Ver link do paciente">
              <IconButton size="small" onClick={() => setDialogOpen(true)}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {teleconsultDialog}
    </>
  );
};

export default TeleconsultButton;
